import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';

const MODULE_ICON = {
    society: 'solar:buildings-3-bold-duotone',
    members: 'solar:users-group-rounded-bold-duotone',
    vendor: 'solar:shop-2-bold-duotone',
    setup: 'solar:settings-bold-duotone',
    finance: 'solar:wallet-money-bold-duotone',
    accounts: 'solar:calculator-bold-duotone',
    reports: 'solar:chart-2-bold-duotone',
    system: 'solar:shield-keyhole-bold-duotone',
    compliance: 'solar:document-text-bold-duotone',
    controller: 'solar:server-bold-duotone',
};
const moduleIcon = (m) => MODULE_ICON[m] || 'solar:layers-bold-duotone';
const titleCase = (s) => String(s || '').replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Group a flat permission catalog by module → resource → permissions[].
const groupCatalog = (catalog = []) => {
    const mods = {};
    catalog.forEach((p) => {
        const m = p.module || 'other';
        const r = p.resource || 'other';
        (mods[m] = mods[m] || {});
        (mods[m][r] = mods[m][r] || []).push(p);
    });
    return mods;
};

const PermissionMatrix = ({ catalog = [], selectedIds, onToggle, onToggleMany, search = '' }) => {
    const [collapsed, setCollapsed] = useState(new Set());
    const grouped = useMemo(() => groupCatalog(catalog), [catalog]);

    const toggleCollapse = (key) => setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const matches = (p) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return p.permission_code?.toLowerCase().includes(q)
            || p.action?.toLowerCase().includes(q)
            || p.resource?.toLowerCase().includes(q)
            || p.description?.toLowerCase().includes(q);
    };

    const modules = Object.entries(grouped);
    if (modules.length === 0) {
        return (
            <div className="text-center py-5 text-secondary-light">
                <Icon icon="solar:shield-keyhole-linear" className="text-3xl mb-2 d-block mx-auto" />
                <p className="text-sm mb-0">No permissions in the catalog.</p>
            </div>
        );
    }

    return (
        <div className="d-flex flex-column gap-2">
            {modules.map(([mod, resources]) => {
                const moduleIds = Object.values(resources).flat().map((p) => p.permission_id);
                const moduleSelected = moduleIds.filter((id) => selectedIds.has(id)).length;
                const moduleAll = moduleIds.length > 0 && moduleSelected === moduleIds.length;
                const moduleSome = moduleSelected > 0 && !moduleAll;
                const isCollapsed = collapsed.has(mod);

                const visibleResources = Object.entries(resources)
                    .map(([resource, perms]) => [resource, perms.filter(matches)])
                    .filter(([, perms]) => perms.length > 0);
                if (search && visibleResources.length === 0) return null;

                return (
                    <div key={mod} className="card radius-8 border shadow-none">
                        <div className="card-header py-2 px-12 bg-neutral-50 border-bottom d-flex align-items-center gap-2">
                            <input type="checkbox" className="form-check-input m-0"
                                checked={moduleAll}
                                ref={(el) => { if (el) el.indeterminate = moduleSome; }}
                                onChange={() => onToggleMany(moduleIds)}
                                onClick={(e) => e.stopPropagation()} />
                            <button type="button" onClick={() => toggleCollapse(mod)}
                                className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2 flex-grow-1 text-start">
                                <Icon icon={isCollapsed ? 'mdi:chevron-right' : 'mdi:chevron-down'} className="text-secondary-light" />
                                <Icon icon={moduleIcon(mod)} className="text-primary-600" />
                                <span className="fw-semibold text-sm">{titleCase(mod)}</span>
                                <span className="badge bg-neutral-200 text-secondary-light fw-medium px-2 py-1 radius-4 text-xs">
                                    {moduleSelected}/{moduleIds.length}
                                </span>
                            </button>
                        </div>

                        {!isCollapsed && (
                            <div className="card-body p-12">
                                {(search ? visibleResources : Object.entries(resources)).map(([resource, perms]) => {
                                    const resourceIds = perms.map((p) => p.permission_id);
                                    const resSelected = resourceIds.filter((id) => selectedIds.has(id)).length;
                                    const resAll = resourceIds.length > 0 && resSelected === resourceIds.length;
                                    const resSome = resSelected > 0 && !resAll;
                                    return (
                                        <div key={resource} className="mb-3">
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <input type="checkbox" className="form-check-input m-0"
                                                    checked={resAll}
                                                    ref={(el) => { if (el) el.indeterminate = resSome; }}
                                                    onChange={() => onToggleMany(resourceIds)} />
                                                <span className="fw-medium text-capitalize text-sm">{titleCase(resource)}</span>
                                                <span className="badge bg-neutral-200 text-secondary-light fw-medium px-2 py-1 radius-4 text-xs">
                                                    {resSelected}/{resourceIds.length}
                                                </span>
                                            </div>
                                            <div className="row gx-2 gy-1 ms-3">
                                                {perms.map((p) => {
                                                    const checked = selectedIds.has(p.permission_id);
                                                    return (
                                                        <div key={p.permission_id} className="col-12 col-sm-6 col-lg-4 col-xxl-3">
                                                            <label
                                                                className={`d-flex align-items-center gap-2 px-2 py-1 radius-4 sysperm-row ${checked ? 'sysperm-row-active' : ''}`}
                                                                title={p.description ? `${p.permission_code} — ${p.description}` : p.permission_code}>
                                                                <input type="checkbox" className="form-check-input m-0"
                                                                    checked={checked} onChange={() => onToggle(p.permission_id)} />
                                                                <span className="text-sm text-capitalize text-truncate">
                                                                    {titleCase(p.action) || p.permission_code}
                                                                </span>
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}

            <style>{`
                .sysperm-row { cursor: pointer; }
                .sysperm-row:hover { background-color: #f3f4f6; }
                .sysperm-row-active { background-color: #eef4ff; }
                .sysperm-row-active:hover { background-color: #dfe9ff; }
            `}</style>
        </div>
    );
};

export default PermissionMatrix;
